import Logo from "@assets/svgs/logo.svg";
import { Typography } from "@cpj-ai/panel-npm-package";
import React from "react";
import { AssessmentDetail } from "./types";

interface AssessmentDetailCardProps {
  assessment: AssessmentDetail;
  variant?: "embedded" | "standalone";
  className?: string;
}

const AssessmentDetailCard: React.FC<AssessmentDetailCardProps> = ({ assessment, variant = "standalone", className }) => {
  if (!assessment) return null;

  const getStatusPillClasses = (value?: string) => {
    switch (value) {
      case "COMPLETED":
        return "text-green-700 bg-green-50 border border-green-200";
      case "IN_PROGRESS":
        return "text-orange-700 bg-orange-50 border border-orange-200";
      case "NOT_STARTED":
        return "text-blue-700 bg-blue-50 border border-blue-200";
      default:
        return "text-gray-600 bg-gray-50 border border-gray-200";
    }
  };

  const { title, description, status, remainingQuestions, totalEstimatedTime, totalQuestions, skill, results } =
    assessment || {};

  const primaryResult = results && results.length > 0 ? results[0] : undefined;
  const evaluation = primaryResult?.evaluation;
  const scoreDisplay =
    evaluation && evaluation.score !== undefined && evaluation.score !== null ? evaluation.score : undefined;

  const answeredQuestions =
    typeof totalQuestions === "number" && typeof remainingQuestions === "number"
      ? Math.max(totalQuestions - remainingQuestions, 0)
      : undefined;
  const completionPercent =
    typeof totalQuestions === "number" && totalQuestions > 0 && typeof answeredQuestions === "number"
      ? Math.round((answeredQuestions / totalQuestions) * 100)
      : undefined;

  const containerClasses =
    variant === "standalone"
      ? ` rounded-2xl border border-gray-100 shadow-sm print:shadow-none print:border-gray-300 overflow-hidden relative ${className ?? ""}`
      : `w-full relative ${className ?? ""}`;

  return (
    <div className={containerClasses}>
      <div
        className={`w-full bg-gradient-to-r from-purple-50 via-white to-blue-50 ${variant === "standalone" ? "border-b border-purple-100" : "rounded-t-2xl border-b border-purple-100"
          } px-5 py-4 relative overflow-hidden`}
      >
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Typography className="text-xs font-semibold text-purple-700 mb-1">AI Assessment Overview</Typography>
            <Typography className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {title || "Assessment details"}
            </Typography>
            {skill?.title && (
              <Typography className="text-xs text-gray-600 mt-1 truncate">
                Focus: <span className="font-medium">{skill.title}</span>
              </Typography>
            )}
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0 pe-4">
            {status && (
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-white ${getStatusPillClasses(
                  status,
                )}`}
              >
                {status === "COMPLETED"
                  ? "Completed"
                  : status === "IN_PROGRESS"
                    ? "In Progress"
                    : status === "NOT_STARTED"
                      ? "Not Started"
                      : status}
              </span>
            )}

            {typeof scoreDisplay !== "undefined" && (
              <div className="flex items-center gap-3 bg-white rounded-2xl border border-purple-200/60 px-4 py-2.5">
                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Overall score</span>
                <span className="text-2xl font-bold tabular-nums bg-gradient-to-r from-purple-700 to-blue-700 bg-clip-text text-transparent">
                  {scoreDisplay}
                  <span className="text-sm font-semibold text-gray-400 ml-0.5">/100</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={variant === "standalone" ? "p-5" : "px-0"}>
        {/* Faint rotated watermark behind assessment body (visible on white background / print) */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10">

          <img src={Logo} alt="logo watermark" className="w-60 h-auto -rotate-45" />

        </div>

        <div className={variant === "standalone" ? "relative flex flex-col gap-6 py-1" : "relative flex flex-col gap-6 py-1 px-5"}>
          <section className="rounded-2xl border border-gray-100 shadow-sm print:shadow-none print:border-gray-300 overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100">
              <div className="bg-white px-3 py-2.5 sm:px-4 sm:py-3">
                <Typography className="text-xs sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  Remaining
                </Typography>
                <div className="flex items-center gap-2">
                  <Typography className="text-base sm:text-lg font-bold text-gray-900 tabular-nums mt-0.5">
                    {typeof remainingQuestions === "number" ? remainingQuestions : "-"}
                  </Typography>
                  <Typography className="text-xs text-gray-400">questions</Typography>
                </div>
              </div>
              <div className="bg-white px-3 py-2.5 sm:px-4 sm:py-3">
                <Typography className="text-xs sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  Total
                </Typography>
                <div className="flex items-center gap-2">
                  <Typography className="text-base sm:text-lg font-bold text-gray-900 tabular-nums mt-0.5">
                    {typeof totalQuestions === "number" ? totalQuestions : "-"}
                  </Typography>
                  <Typography className="text-xs text-gray-400">questions</Typography>
                </div>
              </div>
              <div className="bg-white px-3 py-2.5 sm:px-4 sm:py-3">
                <Typography className="text-xs sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  Est. time
                </Typography>
                <Typography className="text-base sm:text-lg font-bold text-gray-900 tabular-nums mt-0.5">
                  {typeof totalEstimatedTime === "number" ? (totalEstimatedTime / 60).toFixed(1) : "-"}
                  <span className="text-xs font-medium text-gray-500 ms-1">min</span>
                </Typography>
              </div>
              <div className="bg-white px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col justify-center">
                {typeof completionPercent === "number" ? (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <Typography className="text-xs font-medium text-gray-500 uppercase tracking-wide">Done</Typography>
                      <Typography className="text-xs font-bold text-gray-900 tabular-nums">
                        {completionPercent}%
                      </Typography>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-500"
                        style={{ width: `${completionPercent}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Typography className="text-xs font-medium text-gray-500 uppercase tracking-wide">Done</Typography>
                    <Typography className="text-sm font-bold text-gray-900 tabular-nums mt-0.5">—</Typography>
                  </>
                )}
              </div>
            </div>
          </section>

          <section>
            <div className=" rounded-2xl border border-gray-100 p-4 shadow-sm print:shadow-none print:border-gray-300">
              <Typography className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Summary</Typography>
              {description ? (
                <Typography className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {description}
                </Typography>
              ) : (
                <Typography className="text-xs text-gray-400">No description provided.</Typography>
              )}

              {skill && (skill.title || skill.description) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Typography className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Skill focus
                  </Typography>
                  {skill.title && (
                    <Typography className="text-sm font-semibold text-gray-900 mb-0.5">{skill.title}</Typography>
                  )}
                  {skill.description && (
                    <Typography className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                      {skill.description}
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </section>

          {evaluation && (
            <section className=" rounded-2xl border border-gray-100 p-4 shadow-sm print:shadow-none print:border-gray-300 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <Typography className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Evaluation summary
                </Typography>
                {evaluation.scoreValue ? (
                  <span className="px-2 py-1 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-100">
                    {String(evaluation.scoreValue)}
                  </span>
                ) : (
                  <Typography className="text-xs text-gray-400">-</Typography>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Score
                  </Typography>
                  <Typography className="text-lg font-bold text-gray-900">
                    {typeof scoreDisplay !== "undefined" ? scoreDisplay : "-"}
                  </Typography>
                </div>

                {evaluation.userFeedback && (
                  <div>
                    <Typography className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Candidate feedback
                    </Typography>
                    <Typography className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {evaluation.userFeedback}
                    </Typography>
                  </div>
                )}
              </div>

              {evaluation.scoreReasoning && (
                <div className="pt-3 border-t border-gray-100">
                  <Typography className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    AI reasoning
                  </Typography>
                  <Typography className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {evaluation.scoreReasoning}
                  </Typography>
                </div>
              )}

              {(evaluation.positivePoints && evaluation.positivePoints.length > 0) ||
                (evaluation.negativePoints && evaluation.negativePoints.length > 0) ? (
                <div className="pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {evaluation.positivePoints && evaluation.positivePoints.length > 0 && (
                    <div>
                      <Typography className="text-[11px] font-semibold text-green-600 uppercase tracking-wide mb-1">
                        Strengths
                      </Typography>
                      <ul className="list-disc pl-4 space-y-1">
                        {evaluation.positivePoints.map(
                          (point, index) =>
                            point && (
                              <li key={index}>
                                <Typography className="text-xs text-gray-700 leading-relaxed">{point}</Typography>
                              </li>
                            ),
                        )}
                      </ul>
                    </div>
                  )}

                  {evaluation.negativePoints && evaluation.negativePoints.length > 0 && (
                    <div>
                      <Typography className="text-[11px] font-semibold text-red-600 uppercase tracking-wide mb-1">
                        Considerations
                      </Typography>
                      <ul className="list-disc pl-4 space-y-1">
                        {evaluation.negativePoints.map(
                          (point, index) =>
                            point && (
                              <li key={index}>
                                <Typography className="text-xs text-gray-700 leading-relaxed">{point}</Typography>
                              </li>
                            ),
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </section>
          )}

          {results && results.length > 0 && (
            <section className="space-y-2">
              <Typography className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Questions & responses
              </Typography>
              <div className="space-y-2">
                {results.map((result, index) => {
                  if (!result) return null;
                  const questionText = result.question?.text;
                  const feedback = result.evaluation?.userFeedback;
                  if (!questionText && !feedback) return null;

                  return (
                    <div
                      key={index}
                      className=" rounded-2xl border border-gray-100 p-3 shadow-sm print:shadow-none print:border-gray-300 flex flex-col gap-1"
                    >
                      {questionText && (
                        <Typography className="text-xs font-semibold text-gray-900 leading-snug">
                          {index + 1}. {questionText}
                        </Typography>
                      )}
                      {feedback ? (
                        <Typography className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                          {feedback}
                        </Typography>
                      ) : (
                        <Typography className="text-xs italic text-default-400">
                          No answer provided
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Footer logo row at end of each assessment */}
        <div className="mt-6 flex justify-end items-center gap-2">
          <img src={Logo} alt="logo" className="w-8 h-auto" />
          <Typography className="text-xs font-medium text-primary-500 mt-1">Canada Prime Jobs</Typography>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetailCard;

